import ResponsiveToolkit from 'responsive-toolkit/dist/bootstrap-toolkit.min';

import Backbone from '@rover/globals/backbone';
import $ from '@rover/globals/jquery';
import Mustache from '@rover/globals/Mustache';
import Rover from '@rover/globals/Rover';
import _ from '@rover/globals/underscore';
import { SERVICE_ADD_ON_UNITS } from '@rover/shared/js/constants/service';
import {
  getExtendedCareDescription,
  getExtendedCareHours,
} from '@rover/utilities/addonDescription';
import { formatCurrency } from '@rover/utilities/currency';
import inject from '@rover/utilities/injector';
import i18n from '@rover/utilities/translation';

export default Backbone.View.extend({
  rendered: false,
  priceField: 'amountToPay',
  initialize(options) {
    _.bindAll(this, 'render', 'modifyTotalPrice');
    this.priceModel = options.priceModel;
    this.listenTo(this.model, 'complete', this.render);
    this.listenTo(this.priceModel, 'change', this.render);

    // For app embedded price breakdown
    if (typeof options.showSummary === 'undefined') {
      this.showSummary = true;
    } else {
      this.showSummary = options.showSummary;
    }
  },
  modifyTotalPrice(initial) {
    // Only modify the price display if this is rendered
    // This will be called before render happens if the wallet
    // loads before the conversation and updates the conversation model
    if (!this.rendered) {
      return;
    }

    const priceBreakdown = this.priceModel.getTotalPrice();

    // Only modify the price if we know what the total roverBucksBalance is
    if (!priceBreakdown) {
      return;
    }

    if (Rover.context.isSitter) {
      $('.js-total-price').text(priceBreakdown.stayPriceDisplay);
    } else {
      if (priceBreakdown.roverBucksToUse !== '0.00') {
        this.$('.js-roverbucks-amount').text(priceBreakdown.roverBucksToUseDisplay);
        if (initial !== true) {
          this.$('.js-roverbucks-amount').parent().removeClass('highlight').addClass('highlight');
        }
        this.$('.js-has-roverbucks').show();
      } else {
        this.$('.js-has-roverbucks').hide();
      }
      $('.js-total-price').text(priceBreakdown[`${this.priceField}Display`]);
    }
    $('.js-total-price-container').show();
    if (initial !== true) {
      $('.js-total-price').parent().removeClass('highlight').addClass('highlight');
    }
  },
  isRendered() {
    return this.rendered;
  },
  formatBookingAddOns(addOns) {
    const petNameUrlMap = {};

    _.each(this.model.get('pets'), (dog) => {
      petNameUrlMap[dog.url] = {
        name: dog.name,
        webUrl: dog.web_url,
      };
    });

    const stayCurrency = this.priceModel.requestableModel.get('stay_price_obj').currency_iso;

    return addOns.map((addOn, i) => {
      const newInfo = {};
      if (addOn.pet) {
        newInfo.dogName = petNameUrlMap[addOn.pet].name;
        newInfo.dogUrl = petNameUrlMap[addOn.pet].webUrl;
      }

      const locale = inject('Rover.context.language', undefined);
      newInfo.price_per_unit = formatCurrency(addOn.price_per_unit, stayCurrency, locale);
      newInfo.total_price = formatCurrency(addOn.total_price, stayCurrency, locale);

      newInfo.unit = i18n._(
        SERVICE_ADD_ON_UNITS[addOn.unit]
          ? SERVICE_ADD_ON_UNITS[addOn.unit](addOn.num_units)
          : addOn.unit_description
      );

      if (i > 0) newInfo.notFirst = true;
      newInfo.oneTimeEvent = addOn.frequency === 'one_time_event';
      newInfo.perUnitEvent = addOn.frequency === 'per_unit_event';
      return { ...addOn, ...newInfo };
    });
  },

  getContextData() {
    let data = this.model.toJSON();
    data.isMobileEmbedded = Rover.context.isMobileEmbedded;
    data.roverFeesLocalizedLink = Rover.context.roverFeesLocalizedLink;
    data.structuredDateTime = Rover.context.structuredDateTime;
    data.showSummary = this.showSummary;

    if (this.model.get('pets')) {
      data.dogLinks = this.model
        .get('pets')
        .map((pet) => {
          const renderablePet = { ...pet };

          if (Rover.context.isMobileEmbedded !== undefined) {
            renderablePet.isMobileEmbedded = Rover.context.isMobileEmbedded;
          }

          return Mustache.template('new_design/account_conversations/pet').render(renderablePet);
        })
        .join(', ');
    }

    data.alternativeRateAddOns = this.formatBookingAddOns(this.model.getAlternativeRateAddOns());

    data.nonAlternativeRateAddOns = this.formatBookingAddOns(
      this.model.getNonAlternativeRateAddOns()
    ).map((addOn) => {
      if (addOn.slug === 'extended-care') {
        return {
          ...addOn,
          // eslint-disable-next-line no-param-reassign
          name: (addOn.name += ` (${getExtendedCareHours(data.alternativeRateAddOns, addOn)})`),
          descriptionOverride: getExtendedCareDescription(data.alternativeRateAddOns, addOn),
        };
      }
      return addOn;
    });

    if (!Rover.context.isSitter) {
      data.nonAlternativeRateAddOns = data.nonAlternativeRateAddOns.concat(
        this.formatBookingAddOns(this.model.getActiveRequesterAddOns())
      );
    }

    data.hasNonAlternativeRateAddOns = data.nonAlternativeRateAddOns.length !== 0;
    data.booked = data.status === 'booked';
    data.isSitter = Rover.context.isSitter;
    data.unit = this.model.getUnit();
    const serviceType = this.model.getServiceType();
    data.serviceTypeName = serviceType ? serviceType.name : '';
    data.showPaymentInformation = data.booked && !serviceType.supports_sdv_based_payments;

    data = { ...data, ...this.getStructuredDateTimeFormats() };

    return data;
  },

  getStructuredDateTimeFormats() {
    const serviceType = this.model.getServiceType();
    const conversation =
      'getActiveConversation' in this.model ? this.model.getActiveConversation() : this.model;
    return {
      serviceType,
      locationDescription: inject('Rover.context.locationDescription', ''),
      lengthDescription: inject('Rover.context.lengthDescription', []),
      recurrenceDescription: inject('Rover.context.recurrenceDescription', ''),
      durationDescription: inject('Rover.context.durationDescription', ''),
      dogBoardingOrHouseSitting:
        conversation.isOvernightBoarding() || conversation.isOvernightTraveling(),
      dropInOrDogWalking: conversation.isDropIn() || conversation.isDogWalking(),
      commaSeparatedPetNames: this.model
        .get('pets')
        .map((pet) => pet.name)
        .join(', '),
    };
  },

  render() {
    const data = this.getContextData();
    if (data.structuredDateTime) {
      const sdtSummaryTemplate = Mustache.template(
        'js/app/conversations/ConversationPageRequestSummary'
      );
      data.renderedStructuredDateTimeFields = sdtSummaryTemplate.render(data);
    }
    this.$el.html(this.template.render(data));
    this.rendered = true;
    this.modifyTotalPrice(true);
    this.trigger('rendered');
    this.$el.parents('.js-conversation-summary-container').show();

    if (ResponsiveToolkit.is('xs')) {
      this.$('[data-toggle="sticky-popover"]').off('mouseover');
      this.$('[data-toggle="sticky-popover"]').off('mouseleave');
    }

    Rover.stickyPopover(this.$('[data-toggle="sticky-popover"]'), {
      container: 'body',
    });
  },
});



// WEBPACK FOOTER //
// ./js/backbone/views/checkout/requestable_summary_view.js